import React, { useCallback, useEffect, useState } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileCheck, ArrowLeft, AlertCircle, Lock, Loader2 } from 'lucide-react';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { TreasuryCheckFace } from '../../components/pas/TreasuryCheckPrintParts';
import { PayloadFieldsForm, toPayload } from '../../components/pas/PayloadFieldsForm';
import { amountWords } from '../../components/check/cryptoTreasuryFace';
import { handleActionError, actionSuccess } from '../../utils/actionFee';
import '../../components/check/check-print.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check`;
const input = 'w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-emerald-500 outline-none';

const previewCheck = (pre, amount, payee, p) => ({
  check_no: 'PREVIEW', date: pre.date, amount, amount_words: amountWords(amount), network: pre.network, token_symbol: pre.reserve_room_usd != null ? 'BETH' : undefined,
  payee: { name: payee?.name || payee?.email || 'PAYEE' }, payee_address: payee?.wallet || '',
  reference: pre.reference || {}, issuer_seal: '', drawer_tin: pre.drawer_tin, micr: pre.micr,
  payload: { memo: p.memo, services_contract_award: { contract_no: p.services_contract_no }, parties: { seller: pre.drawer } },
});

const Blocked = ({ pre, navigate }) => (
  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6" data-testid="mint-check-blocked">
    <div className="flex items-start gap-3">
      <Lock className="w-5 h-5 text-amber-300 mt-0.5" />
      <div>
        <p className="text-amber-200 font-semibold">{pre.mint_blocked_reason?.startsWith('Weekly') ? 'Weekly check limit reached' : pre.is_officer || pre.fee_waived ? "Mint Check isn't available yet" : 'Mint Check is for Treasury Officer subscribers'}</p>
        <p className="text-amber-100/80 text-sm mt-1" data-testid="mint-check-blocked-reason">{pre.mint_blocked_reason}.</p>
        {pre.mint_blocked_reason?.startsWith('Weekly') ? <p className="text-amber-100/60 text-xs mt-2" data-testid="mint-check-limit-note">Each check frees up a slot 7 days after it was minted.</p> : (
        <button onClick={() => navigate('/pas/banking')} data-testid="mint-check-subscribe-btn"
          className="mt-4 px-5 py-2 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-lg font-semibold text-sm">
          {pre.is_officer || pre.fee_waived ? 'Go to Banking & Credit' : 'Subscribe · $499/month'}
        </button>)}
      </div>
    </div>
  </div>
);

const Header = ({ navigate, network }) => (
  <header className="bg-slate-800 border-b border-slate-700">
    <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
      <button onClick={() => navigate('/pas/dashboard')} className="p-2 text-slate-400 hover:text-white" data-testid="mint-check-back"><ArrowLeft className="w-5 h-5" /></button>
      <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center"><FileCheck className="w-6 h-6 text-white" /></div>
      <div>
        <h1 className="text-lg font-bold text-white">Mint Check</h1>
        <p className="text-xs text-slate-400" data-testid="mint-check-network">Treasury Check · minted on {network === 'base' ? 'Base' : network}</p>
      </div>
    </div>
  </header>
);

export default function CreateCheck() {
  const navigate = useNavigate();
  const { pasClient, token, loading: authLoading } = usePASClientAuth();
  const [pre, setPre] = useState(null);
  const [amount, setAmount] = useState('');
  const [to, setTo] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [payee, setPayee] = useState(null);
  const [p, setP] = useState({});
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchPrefill = useCallback((q = '') => axios.get(`${API}/mint-prefill`, { params: { to: q }, headers: { Authorization: `Bearer ${token}` } }).then(r => r.data), [token]);

  useEffect(() => { if (!authLoading && !pasClient) navigate('/pas/login'); }, [authLoading, pasClient, navigate]);
  useEffect(() => {
    if (!token) return;
    fetchPrefill().then(d => {
      setPre(d);
      setP(v => ({ ...v, services_contract_no: d.services_contract_no, award_date: d.award_date, seller: d.drawer.email, services_description: 'IRC §1031 exchange settlement' }));
    }).catch(() => setPre({ mint_blocked_reason: 'Could not load your account' }));
  }, [token, fetchPrefill]);
  useEffect(() => {
    if (!pre || !to.trim()) { setPayee(null); return undefined; }
    const t = setTimeout(() => fetchPrefill(to.trim()).then(d => setPayee(d.payee)).catch(() => setPayee(null)), 500);
    return () => clearTimeout(t);
  }, [to, pre, fetchPrefill]);
  useEffect(() => { setP(v => ({ ...v, award_amount_usd: amount })); }, [amount]);

  const mint = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/mint`, { network: pre.network, amount: Number(amount), to: to.trim(), payee_name: payeeName.trim(), ...toPayload(p) }, auth);
      actionSuccess(r.data);
      if (r.data.receipt_emailed) toast.info('Mint receipt emailed to the payee, with a copy to you');
      navigate(`/pas/treasury-check/${r.data.check.check_id}`);
    } catch (err) { handleActionError(err, token, 'Mint failed'); }
    finally { setBusy(false); }
  };

  if (authLoading || !pre) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="w-10 h-10 text-emerald-500 animate-spin" /></div>;
  const amt = Number(amount) || 0;
  const noWallet = to.trim() && payee && !payee.wallet;
  return (
    <div className="min-h-screen bg-slate-900" data-testid="mint-check-page">
      <Header navigate={navigate} network={pre.network} />
      <div className="max-w-5xl mx-auto px-4 pt-8">
        <SectionGuide id="mint-check" className="mb-6" />
        <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Live preview</p>
        <div className="overflow-x-auto" data-testid="write-check-preview">
          <div className="check-sheet mx-auto"><TreasuryCheckFace c={previewCheck(pre, amt, payeeName.trim() ? { ...(payee || pre.drawer), name: payeeName.trim() } : (payee || pre.drawer), p)} /></div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {!pre.can_mint ? <Blocked pre={pre} navigate={navigate} /> : (
          <form onSubmit={mint} className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-300 mb-2">Amount (USD) *</label>
                <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="1000" data-testid="mint-check-amount" className={input} />
                <p className="text-xs text-slate-500 mt-1" data-testid="mint-check-available">Available credit: ${Number(pre.available_credit_usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}{pre.fee_waived ? ' · fee waived' : ' · 1% fee by card'}{pre.weekly_limit != null ? ` · ${pre.minted_this_week} of ${pre.weekly_limit} checks used this week` : ''}</p>
                {pre.reserve_room_usd != null && <p className={`text-xs mt-1 ${amt > pre.reserve_room_usd ? 'text-amber-300' : 'text-slate-500'}`} data-testid="mint-check-reserve-room">Reserve room on Base: ${Number(pre.reserve_room_usd).toLocaleString(undefined, { minimumFractionDigits: 2 })}{amt > pre.reserve_room_usd ? ' · amount is above what reserves can back right now' : ''}</p>}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-slate-300 mb-2">Pay to the order of (payee name)</label>
                  <input value={payeeName} onChange={(e) => setPayeeName(e.target.value)} maxLength={120} placeholder="e.g. John Smith or ABC Holdings LLC" data-testid="mint-check-payee-name" className={input} />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2">Payee wallet or subscriber email (optional)</label>
                  <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Blank = check is held in my wallet" data-testid="mint-check-payee" className={input} />
                  {payee?.wallet && <p className="text-xs text-emerald-400 mt-1" data-testid="mint-check-payee-found">{payee.name || 'Wallet'} · {payee.wallet.slice(0, 10)}…{payee.wallet.slice(-6)}</p>}
                  {noWallet && <p className="text-xs text-amber-300 mt-1" data-testid="mint-check-payee-missing">No subscriber wallet found. Enter a 0x wallet address or a subscriber's email, or leave it blank</p>}
                </div>
              </div>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6" data-testid="mint-check-exchange-contract">
              <h3 className="text-white font-semibold">Exchange contract</h3>
              <p className="text-xs text-slate-500">Drawer: {pre.drawer.name} · {pre.drawer.email} · Treasury reference: {pre.reference?.cusip ? `CUSIP ${pre.reference.cusip} (${pre.reference.auction_date})` : 'latest series is registered automatically at mint'}</p>
              <PayloadFieldsForm value={p} onChange={setP} prefix="mint-check" />
            </div>
            {pre.mint_blocked_reason && <p className="text-amber-300 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{pre.mint_blocked_reason}</p>}
            <button type="submit" disabled={busy || !(amt > 0) || noWallet || (pre.reserve_room_usd != null && amt > pre.reserve_room_usd)} data-testid="create-check-submit"
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl py-4 font-bold flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Minting on Base (live)...</> : <><FileCheck className="w-5 h-5" /> Mint Live Base Check for ${amt.toFixed(2)}</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
